import type { StationSessionResponse } from '@jq4race/shared';

// valet-keychain-qr D1 — station-auth. localStorage-backed persistence for
// the STATION DEVICE'S long-lived opaque token (entered once when the
// terminal/tablet is set up, spec "no personal valet login required" — the
// device itself is the credential, not a staff member) and the short-lived
// session JWT minted from it. Deliberately separate from apps/pwa's operator
// auth-store (different identity, different lifecycle — see the api-client
// station.ts header comment for why the HTTP layer is also separate).

const DEVICE_TOKEN_KEY = 'jq4race:station_device_token';
const SESSION_KEY = 'jq4race:station_session';

// Re-mint a bit before the JWT's actual expiry so a lookup in-flight right
// at the boundary never races a 401 (spec doesn't fix this margin — 30s is
// a conservative default relative to the 15-min TTL).
const EXPIRY_SAFETY_MARGIN_MS = 30_000;

export interface CachedStationSession {
  accessToken: string;
  organizationId: string;
  locationId: string;
  /** Epoch ms — computed from the response's expiresIn at cache time. */
  expiresAt: number;
}

export function getDeviceToken(): string | null {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setDeviceToken(token: string): void {
  try {
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch {
    // Best-effort — a private-browsing/storage-disabled tab simply won't persist.
  }
}

/** Forgets BOTH the device token and any cached session — returns to the setup screen. */
export function clearStationDevice(): void {
  try {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // no-op
  }
}

export function getCachedSession(): CachedStationSession | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CachedStationSession;
    if (Date.now() >= parsed.expiresAt - EXPIRY_SAFETY_MARGIN_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function cacheSession(response: StationSessionResponse): CachedStationSession {
  const cached: CachedStationSession = {
    accessToken: response.accessToken,
    organizationId: response.organizationId,
    locationId: response.locationId,
    expiresAt: Date.now() + response.expiresIn * 1000,
  };
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(cached));
  } catch {
    // Best-effort — the session still works for the rest of this page load
    // from the in-memory hook state even if it can't be persisted.
  }
  return cached;
}

export function clearCachedSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // no-op
  }
}
